import React from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { AdminHeader, AdminBody } from "./components/AdminHeader";
import { useAuth } from "@/context/AuthContext";
import { Users, ClipboardList, CheckCircle, Clock, Newspaper, Calendar } from "lucide-react";

const Stat = ({ icon: Icon, label, value, accent }) => (
  <div className="bg-white border border-f2b-filet p-6" data-testid={`stat-${label}`}>
    <div className="flex items-center justify-between mb-3">
      <span className="f2b-kicker text-f2b-gray">{label}</span>
      <Icon size={18} className={accent ? "text-f2b-cyan" : "text-f2b-gray"} />
    </div>
    <div className="font-display font-bold text-f2b-black text-4xl">{value}</div>
  </div>
);

const Dashboard = () => {
  const { user } = useAuth();
  const { data: d, isLoading } = useQuery({ queryKey: ["dashboard"], queryFn: async () => (await api.get("/admin/dashboard")).data });

  return (
    <>
      <AdminHeader title={`Bonjour, ${user?.name?.split(" ")[0] || ""}`} subtitle="Vue d'ensemble de FACADES2build" />
      <AdminBody>
        {isLoading || !d ? <p className="text-f2b-gray">Chargement…</p> : (
          <>
            <div className="mb-6 bg-f2b-black text-white p-6 flex items-center justify-between">
              <div>
                <p className="f2b-kicker text-f2b-cyan mb-1">Édition active</p>
                <p className="font-display font-bold text-2xl">{d.active_edition ? `${d.active_edition.date_label} — ${d.active_edition.city}` : "Aucune"}</p>
              </div>
              {d.active_edition && <span className="text-f2b-gray text-sm">{d.active_edition.theme}</span>}
            </div>

            <h2 className="f2b-kicker text-f2b-gray mb-4">Inscriptions (édition active)</h2>
            {d.capacity != null && (
              <div className="mb-6 bg-white border border-f2b-filet p-5">
                <div className="flex justify-between text-sm mb-2"><span className="text-f2b-text font-medium">Jauge (personnes)</span><span className="text-f2b-gray">{d.occupied} / {d.capacity} places · {d.places_left} restantes</span></div>
                <div className="h-3 bg-f2b-light"><div className="h-full bg-f2b-cyan transition-all" style={{ width: `${Math.min(100, Math.round((d.occupied / d.capacity) * 100))}%` }} /></div>
              </div>
            )}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <Stat icon={Clock} label="À valider" value={d.to_validate} accent />
              <Stat icon={CheckCircle} label="Confirmés" value={d.confirmed} accent />
              <Stat icon={Clock} label="Liste d'attente" value={d.waitlist} />
              <Stat icon={ClipboardList} label="Total demandes" value={d.registrations_total} />
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <Stat icon={Users} label="Arrivés" value={d.arrived} accent />
              <Stat icon={Users} label="Absents" value={d.absent} />
              <Stat icon={Users} label="Taux présence" value={`${d.attendance_rate}%`} accent />
              <Stat icon={ClipboardList} label="Intéressés (prochaine)" value={d.interested} />
            </div>

            <h2 className="f2b-kicker text-f2b-gray mb-4">Contenus</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <Stat icon={Newspaper} label="Articles publiés" value={d.articles_published} accent />
              <Stat icon={Newspaper} label="Brouillons" value={d.articles_drafts} />
              <Stat icon={Users} label="Intervenants" value={d.speakers} />
              <Stat icon={Calendar} label="Éditions" value={d.editions} />
            </div>

            {Object.keys(d.profiles || {}).length > 0 && (
              <>
                <h2 className="f2b-kicker text-f2b-gray mb-4">Répartition par profil</h2>
                <div className="bg-white border border-f2b-filet p-6 space-y-3">
                  {Object.entries(d.profiles).map(([k, v]) => {
                    const total = Object.values(d.profiles).reduce((a, b) => a + b, 0);
                    const pct = total ? Math.round((v / total) * 100) : 0;
                    return (
                      <div key={k}>
                        <div className="flex justify-between text-sm mb-1"><span className="capitalize text-f2b-text">{k}</span><span className="text-f2b-gray">{v} · {pct}%</span></div>
                        <div className="h-2 bg-f2b-light"><div className="h-full bg-f2b-cyan" style={{ width: `${pct}%` }} /></div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </>
        )}
      </AdminBody>
    </>
  );
};

export default Dashboard;
